import React from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatSignOnTimestamp } from '../helpers/utils';

const brand = {
  reachBlue: 'var(--reach-blue)',
  border: 'rgba(5, 5, 51, 0.1)',
  navy: '#050A30',
  borderSubtle: 'rgba(5, 5, 51, 0.08)',
};

function formatAmount(amount) {
  if (amount == null) {
    return '—';
  }

  const num = Number(amount);
  if (Number.isNaN(num)) {
    return String(amount);
  }

  // Host sends minor units (cents): 200 → $2.00, 1200 → $12.00.
  return (num / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function formatTime(time) {
  if (!time) {
    return '—';
  }

  return formatSignOnTimestamp(time);
}

export default function LastTenTransactionsTab({
  transactions = [],
  loading = false,
  error = null,
}) {
  if (loading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} sx={{ color: brand.reachBlue }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Typography variant='body2' color='error' sx={{ textAlign: 'center' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!transactions.length) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ textAlign: 'center' }}
        >
          No transactions available for this terminal.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        height: '100%',
        borderRadius: 2,
        borderColor: brand.borderSubtle,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table size='small' stickyHeader aria-label='last ten transactions'>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  fontFamily: 'BrightstarSans-Bold',
                  color: brand.navy,
                  backgroundColor: 'rgba(5, 10, 48, 0.04)',
                  borderBottomColor: brand.border,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <TableCell>Transaction Time</TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell>Transaction Type</TableCell>
              <TableCell align='right'>Transaction Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction, index) => (
              <TableRow
                key={transaction.serialNumberString ?? `transaction-${index}`}
                sx={{
                  '& .MuiTableCell-root': {
                    fontFamily: 'BrightstarSans-Regular',
                    color: brand.reachBlue,
                    borderBottomColor: brand.border,
                  },
                }}
              >
                <TableCell>{formatTime(transaction.transactionTime)}</TableCell>
                <TableCell>{transaction.productName ?? '—'}</TableCell>
                <TableCell>
                  {transaction.transactionTypeString ?? '—'}
                </TableCell>
                <TableCell
                  align='right'
                  sx={{ fontFamily: 'BrightstarSans-Bold' }}
                >
                  {formatAmount(transaction.transactionAmount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
